/**
 * Сцены для холста сверки с дизайном.
 *
 * Чтобы посмотреть любой экран, не проходя регистрацию и не дожидаясь нужной фазы,
 * приложение можно открыть с параметром `?scene=имя`. Сцена кладёт в хранилище
 * заранее известное состояние и открывает нужную вкладку. В обычной работе параметр
 * никто не передаёт, и этот код не делает ничего.
 *
 * Все моменты времени считаются от «сейчас», поэтому сцена выглядит одинаково
 * в любой день: «таймер в третьей фазе» — это всегда третья фаза.
 */

const WEEK = 7 * 24 * 3600 * 1000
const MINUTE = 60_000

/** Профиль «Паша, 10 недель» — тот же, что во всех макетах. */
function pasha(now: number): Record<string, string> {
  return {
    // Устройством уже пользовались: тур позади
    tour_seen: '1',
    child_name: 'Паша',
    child_birth: String(now - 10 * WEEK),
    child_gender: 'BOY',
    gas_variant: 'true',
    child_birth_weight: '3420',
    profile_updated_at: String(now),
  }
}

/**
 * Две недели окон Паши: по пять в день, от 60 к 72 минутам, с парой поздних стартов
 * (таймер включили на третьей фазе) и одним окном без ответа «уснул».
 */
function wakeHistory(now: number): Record<string, string> {
  const окна: number[] = []
  const события: Array<Record<string, number | string>> = []
  for (let d = 14; d >= 1; d -= 1) {
    const день = new Date(now)
    день.setDate(день.getDate() - d)
    день.setHours(7, 0, 0, 0)
    for (let i = 0; i < 5; i += 1) {
      const at = день.getTime() + i * 165 * MINUTE + ((d * 7 + i * 3) % 11) * MINUTE
      const минут = 58 + Math.round((14 - d) * 0.9) + ((d * 5 + i * 7) % 11) - 5 + (i === 4 ? 5 : 0)
      окна.push(at)
      if (d === 3 && i === 2) {
        события.push({ id: at + 50 * MINUTE, kind: 'window_end', at: at + 50 * MINUTE, window: at, reason: 'awake' })
        continue
      }
      if (i === 1 && d % 4 === 0) события.push({ id: at + 40_000, kind: 'phase', at: at + 40_000, window: at, index: 2, title: 'Лёгкая игра', planned: 10 })
      события.push({ id: at + минут * MINUTE, kind: 'window_end', at: at + минут * MINUTE, window: at, reason: 'asleep' })
    }
  }
  const поМесяцам: Record<string, unknown[]> = {}
  for (const e of события) (поМесяцам[`events_${today(e.at as number)}`] ??= []).push(e)
  const окнаПоМесяцам: Record<string, number[]> = {}
  for (const w of окна) (окнаПоМесяцам[`windows_${today(w)}`] ??= []).push(w)
  return Object.fromEntries([...Object.entries(окнаПоМесяцам), ...Object.entries(поМесяцам)].map(([k, v]) => [k, JSON.stringify(v)]))
}

/** Взвешивания Паши, как в макетах «Аналитика»: 4 950 → 5 520 → 5 900 г. */
function weighings(now: number): Record<string, string> {
  const точки = [
    [now - 6 * WEEK, 4950],
    [now - 4 * WEEK, 5520],
    [now - 2 * WEEK, 5900],
  ] as const
  const поМесяцам: Record<string, Array<Record<string, number | string>>> = {}
  for (const [at, grams] of точки) {
    const ключ = `events_${today(at)}`
    ;(поМесяцам[ключ] ??= []).push({ id: at, kind: 'weight', at, window: at, grams })
  }
  return Object.fromEntries(Object.entries(поМесяцам).map(([k, v]) => [k, JSON.stringify(v)]))
}

/**
 * Двое малышей на устройстве: Соня и Паша. Ключи сразу разложены по приставкам —
 * так их видит приложение после переезда со старой раскладки, и переносить нечего.
 *
 * У Сони окно идёт полчаса, у Паши — нет: на холсте видно, что переключение меняет
 * экран целиком, а не только имя в шапке.
 */
function twoKids(now: number, current: 'sonya' | 'pasha'): Record<string, string> {
  const общие = (id: string, name: string, gender: string, weeks: number): Record<string, string> => ({
    [`kid.${id}.child_name`]: name,
    [`kid.${id}.child_birth`]: String(now - weeks * WEEK),
    [`kid.${id}.child_gender`]: gender,
    [`kid.${id}.gas_variant`]: 'false',
    [`kid.${id}.profile_updated_at`]: String(now),
  })
  return {
    tour_seen: '1',
    kids: JSON.stringify([{ id: 'sonya', name: 'Соня' }, { id: 'pasha', name: 'Паша' }]),
    kid_current: current,
    ...общие('sonya', 'Соня', 'GIRL', 10),
    ...общие('pasha', 'Паша', 'BOY', 30),
    'kid.sonya.window_start': String(now - 30 * MINUTE),
    'kid.sonya.window_updated_at': String(now),
    [`kid.sonya.windows_${today(now)}`]: JSON.stringify([now - 30 * MINUTE]),
  }
}

function signedIn(): Record<string, string> {
  return { session: 'canvas-session', email: 'you@example.com' }
}

function today(now: number): string {
  const d = new Date(now)
  return `${d.getFullYear()}_${String(d.getMonth() + 1).padStart(2, '0')}`
}

/** Тот же расчёт, что в календаре: день по местной дате, а не по всемирному времени. */
function epochDay(now: number): number {
  const d = new Date(now)
  return Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86_400_000)
}

export interface Scene {
  /** Подпись на холсте. */
  title: string
  /** Ключи хранилища (без префикса `moonwake.`). */
  storage: (now: number) => Record<string, string>
  /** Вкладка или подэкран, который надо открыть. */
  open?: 'timer' | 'myDay' | 'calendar' | 'calendar-weight' | 'calendar-wake' | 'calendar-day' | 'menu' | 'articles' | 'profile' | 'contacts' | 'welcome' | 'welcome-2' | 'welcome-3' | 'register' | 'tour-1' | 'tour-2' | 'tour-3' | 'tour-4' | 'splash' | 'register-date' | 'signin' | 'code' | 'code-expired'
}

export const SCENES: Record<string, Scene> = {
  splash: {
    title: 'Загрузка',
    storage: () => ({}),
    open: 'splash',
  },
  welcome: {
    title: 'Приветствие',
    storage: () => ({}),
    open: 'welcome',
  },
  signin: {
    title: 'Вход по почте',
    storage: () => ({}),
    open: 'signin',
  },
  code: {
    title: 'Код из письма',
    storage: () => ({}),
    open: 'code',
  },
  'code-expired': {
    title: 'Код · письмо не пришло',
    storage: () => ({}),
    open: 'code-expired',
  },
  'welcome-2': {
    title: 'Приветствие 2/3',
    storage: () => ({}),
    open: 'welcome-2',
  },
  'welcome-3': {
    title: 'Приветствие 3/3',
    storage: () => ({}),
    open: 'welcome-3',
  },
  register: {
    title: 'О малыше',
    storage: () => ({}),
    open: 'register',
  },
  'register-date': {
    title: 'Дата рождения',
    storage: () => ({}),
    open: 'register-date',
  },
  'timer-idle': {
    title: 'Таймер · до начала',
    storage: (now) => pasha(now),
    open: 'timer',
  },
  'timer-running': {
    title: 'Таймер · фаза 3 из 5',
    // Программа при газиках: 12 + 14 + 10 + … Начало 30 минут назад — середина третьей фазы.
    storage: (now) => ({
      ...pasha(now),
      window_start: String(now - 30 * MINUTE),
      window_updated_at: String(now),
      [`windows_${today(now)}`]: JSON.stringify([now - 30 * MINUTE]),
    }),
    open: 'timer',
  },
  'timer-overtime': {
    title: 'Таймер · дольше плана',
    storage: (now) => ({
      ...pasha(now),
      window_start: String(now - 90 * MINUTE),
      window_updated_at: String(now),
    }),
    open: 'timer',
  },
  'timer-auto-closed': {
    // Окно четыре часа назад: план вышел, ответа не было — закрылось само
    title: 'Таймер · закрылось само',
    storage: (now) => ({
      ...pasha(now),
      window_start: String(now - 240 * MINUTE),
      window_updated_at: String(now),
    }),
    open: 'timer',
  },
  'my-day': {
    title: 'Мой день',
    storage: (now) => pasha(now),
    open: 'myDay',
  },
  calendar: {
    title: 'Календарь с заметками',
    storage: (now) => ({
      ...pasha(now),
      [`windows_${today(now)}`]: JSON.stringify([now - 9 * 3600_000, now - 5 * 3600_000, now - 90 * MINUTE]),
      [`notes_${today(now)}`]: JSON.stringify([
        { id: 1, day: epochDay(now), time: now - 4 * 3600_000, tag: 'FIRST', text: 'Засмеялся вслух, когда папа чихнул' },
        { id: 2, day: epochDay(now) - 2, time: now - 2 * 86_400_000, tag: 'SLEEP', text: 'Ночь без пробуждений' },
      ]),
    }),
    open: 'calendar',
  },
  'calendar-day': {
    // Вчера: три окна с ответами «уснул» и одно ночное пробуждение — сон посчитан точно
    title: 'Лист по дате · сон, окна, ночь',
    storage: (now) => {
      const d = new Date(now)
      d.setDate(d.getDate() - 1)
      d.setHours(7, 0, 0, 0)
      const t0 = d.getTime()
      const окна = [t0, t0 + 3 * 3600_000, t0 + 6 * 3600_000, t0 + 9 * 3600_000]
      const ночь = t0 + 20 * 3600_000
      const события = [
        ...окна.map((w) => ({ id: w + 70 * MINUTE, kind: 'window_end', at: w + 70 * MINUTE, window: w, reason: 'asleep' })),
        // Второе окно: таймер включили на третьей фазе — начало расчётное, в листе «≈»
        { id: окна[1]! + 40_000, kind: 'phase', at: окна[1]! + 40_000, window: окна[1]!, index: 2, title: 'Лёгкая игра', planned: 10 },
        { id: ночь, kind: 'night_wake', at: ночь, window: ночь },
        { id: ночь + 1, kind: 'night_wake_end', at: ночь + 15 * MINUTE, window: ночь, reason: 'asleep' },
        { id: t0 + 5 * 3600_000, kind: 'weight', at: t0 + 5 * 3600_000, window: t0 + 5 * 3600_000, grams: 5900 },
      ]
      const поМесяцам: Record<string, unknown[]> = {}
      for (const e of события) (поМесяцам[`events_${today(e.at)}`] ??= []).push(e)
      return {
        ...pasha(now),
        [`windows_${today(t0)}`]: JSON.stringify([...окна, t0 + 86_400_000]),
        ...Object.fromEntries(Object.entries(поМесяцам).map(([k, v]) => [k, JSON.stringify(v)])),
        calendar_day: String(t0),
      }
    },
    open: 'calendar-day',
  },
  'calendar-wake': {
    title: 'Аналитика · бодрствование',
    storage: (now) => ({ ...pasha(now), ...wakeHistory(now) }),
    open: 'calendar-wake',
  },
  'calendar-weight': {
    title: 'Аналитика · вес в норме',
    storage: (now) => ({ ...pasha(now), ...weighings(now) }),
    open: 'calendar-weight',
  },
  'profile-weight': {
    title: 'Профиль · вес при рождении и последний',
    storage: (now) => ({ ...pasha(now), ...weighings(now), ...signedIn() }),
    open: 'profile',
  },
  'tour-1': { title: 'Тур · 1 из 4', storage: (now) => pasha(now), open: 'tour-1' },
  'tour-2': { title: 'Тур · 2 из 4', storage: (now) => pasha(now), open: 'tour-2' },
  'tour-3': { title: 'Тур · 3 из 4', storage: (now) => pasha(now), open: 'tour-3' },
  'tour-4': { title: 'Тур · 4 из 4', storage: (now) => pasha(now), open: 'tour-4' },
  menu: {
    title: 'Меню',
    storage: (now) => ({ ...pasha(now), window_start: String(now - 30 * MINUTE) }),
    open: 'menu',
  },
  'kids-timer': {
    title: 'Таймер · двое малышей',
    storage: (now) => twoKids(now, 'sonya'),
    open: 'timer',
  },
  'kids-timer-second': {
    title: 'Таймер · второй малыш',
    storage: (now) => twoKids(now, 'pasha'),
    open: 'timer',
  },
  'kids-switch': {
    title: 'Малыши · выбор',
    storage: (now) => twoKids(now, 'sonya'),
    open: 'timer',
  },
  'kids-profile': {
    title: 'Профиль · двое малышей',
    storage: (now) => ({ ...twoKids(now, 'sonya'), ...signedIn() }),
    open: 'profile',
  },
  articles: {
    title: 'Статьи',
    storage: (now) => pasha(now),
    open: 'articles',
  },
  profile: {
    title: 'Профиль',
    storage: (now) => ({ ...pasha(now), ...signedIn() }),
    open: 'profile',
  },
  contacts: {
    title: 'Контакты',
    storage: (now) => ({ ...pasha(now), ...signedIn() }),
    open: 'contacts',
  },
}

/** Что открыть по сцене. Вне сборки с холстом — ничего. */
export function sceneOpen(): Scene['open'] | null {
  if (!__CANVAS__) return null
  const name = sceneFromUrl()
  return name === null ? null : (SCENES[name]?.open ?? null)
}

/** Сцена «Загрузка»: приложение не стартует, экран загрузки остаётся на месте. */
export function sceneHoldsSplash(): boolean {
  return sceneFromUrl() === 'splash'
}

/**
 * Своя ли это машина. Сцены подкладывают состояние в обход регистрации, и на боевом
 * сайте им делать нечего: по ссылке `?scene=…` посторонний видел бы экраны приложения
 * без входа. Прогоны Playwright ходят на 127.0.0.1, поэтому им ничего не мешает.
 */
function ownMachine(): boolean {
  const host = globalThis.location?.hostname ?? ''
  return host === 'localhost' || host === '127.0.0.1' || host === '::1' || host === '[::1]' || host.endsWith('.local')
}

/** Имя сцены из адреса, если есть. В боевой сборке сцен нет вовсе. */
export function sceneFromUrl(): string | null {
  if (!__CANVAS__ || !ownMachine()) return null
  const name = new URLSearchParams(globalThis.location?.search ?? '').get('scene')
  return name !== null && name in SCENES ? name : null
}

/**
 * Хранилище в памяти вместо `localStorage` — с интерфейсом `Storage`, чтобы остальной код
 * ничего не заметил. Нужно холсту: на одной странице десяток рамок с разными сценами, а
 * `localStorage` у них общий, и сцены перезаписывали бы друг друга — рамка «Приветствие»
 * читала бы профиль, который положила соседняя рамка с таймером.
 */
class MemoryStorage implements Storage {
  private map = new Map<string, string>()
  get length(): number {
    return this.map.size
  }
  key(index: number): string | null {
    return [...this.map.keys()][index] ?? null
  }
  getItem(key: string): string | null {
    return this.map.get(key) ?? null
  }
  setItem(key: string, value: string): void {
    this.map.set(key, String(value))
  }
  removeItem(key: string): void {
    this.map.delete(key)
  }
  clear(): void {
    this.map.clear()
  }
}

/** Подменяет хранилище страницы на собственное, изолированное. Возвращает его. */
function isolateStorage(): Storage {
  const memory = new MemoryStorage()
  Object.defineProperty(globalThis, 'localStorage', { value: memory, configurable: true })
  return memory
}

/** Кладёт сцену в своё хранилище. Вызывать до старта приложения. */
export function applyScene(name: string): Scene {
  const scene = SCENES[name]!
  // Сюда не попасть без флага сборки: sceneFromUrl() до этого вернёт null.
  const storage = isolateStorage()
  const now = Date.now()
  for (const [key, value] of Object.entries(scene.storage(now))) storage.setItem('moonwake.' + key, value)
  return scene
}
